/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

export type HubSpotDealUpdateResponse = {
  success: boolean;
  errorMessage: string;
};

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  HubSpotDealUpdateResponse | undefined,
  (requestParams: vp.HubSpotDealUpdateRequest) => void,
  () => void,
];

function usePostHubSpotDealUpdate({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.HubSpotDealUpdateRequest>();
  const [response, setResponse] = useState<HubSpotDealUpdateResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: vp.HubSpotDealUpdateRequest) => {
      setDataFetchingInfo(true, 'sending hubspot data', 'fullScreen');

      const { Success, ErrorMessage } = await api.postHubSpotDealUpdate(buildParams(requestParams), source);

      const res = {
        success: Success,
        errorMessage: ErrorMessage || '',
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams?: vp.HubSpotDealUpdateRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: vp.HubSpotDealUpdateRequest): vp.HubSpotDealUpdateRequest {
    const { DealID, CompanyWebsite, PlatformUsage, CompanyPhone, SocialMediaPage } = requestParams;
    // add information for user
    return vp.removeEmptyProperty<vp.HubSpotDealUpdateRequest>({
      DealID,
      CompanyWebsite: CompanyWebsite ? vp.sanatizeURL(CompanyWebsite) : CompanyWebsite,
      PlatformUsage,
      CompanyPhone,
      SocialMediaPage: SocialMediaPage ? vp.sanatizeURL(SocialMediaPage) : SocialMediaPage,
    });
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostHubSpotDealUpdate);
